<template>
  <v-card>
    <v-row no-gutters>
      <v-col
        cols="8"
        class="px-0"
      >
        <slot name="navigation" />
      </v-col>
    </v-row>

    <v-row 
      class="px-3"
      no-gutters
    >
      <v-col
        cols="12"
        class="py-0"
      >
        <div class="ma-0">
          <v-row no-gutters>
            <v-col class="ma-auto">
              <span class="font-weight-bold"><u>COMPUTATION SUMMARY</u></span>
            </v-col>

            <v-col
              v-if="explanationReportPayrollCycleEmployees.length > 0"
              cols="auto"
              class="mr-8 pt-4"
            >
              <payroll-payroll-cycle-computation-dialog-list-missing-explanation-payroll-cycle-employees
                :payroll-cycle-transaction-id="payrollTransactionId"
                :payroll-cycle-employees="explanationReportPayrollCycleEmployees"
                @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
              />
            </v-col>

            <v-col
              v-if="inactivePayrollCycleEmployees.length > 0" 
              cols="auto"
              class="mr-8 pt-4"
            >
              <payroll-payroll-cycle-computation-dialog-remove-inactive-payroll-cycle-employees
                :payroll-cycle-transaction-id="payrollTransactionId"
                @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
              />
            </v-col>

            <v-col cols="3">
              <v-text-field
                v-model="search"
                label="Search"
                append-icon="search"
              />
            </v-col>
          </v-row>
        </div>
      </v-col>

      <v-col cols="12">
        <v-skeleton-loader
          :loading="loading"
          type="table"
          class="mx-auto"
        >
          <v-data-table
            :items="computationSummaries"
            :headers="headers"
            :single-expand="true"
            show-expand
            :search="search"
            dense
          >
            <template v-slot:item="props">
              <tr>
                <td :class="props.isExpanded?'remove-bottom-border':'border-bottom'">
                  <v-btn
                    icon
                    @click="props.expand(!props.isExpanded)"
                  >
                    <v-icon small> 
                      {{ props.isExpanded ? 'fa-chevron-up':'fa-chevron-down' }} 
                    </v-icon>
                  </v-btn>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border':'border-bottom' ">
                  <u
                    class="font-weight-bold cursor-pointer"
                    @click="props.expand(!props.isExpanded)"
                  >
                    {{ props.item.company_employee.company.name }}
                  </u>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border':'border-bottom' ">
                  <u
                    class="font-weight-bold cursor-pointer"
                    @click="props.expand(!props.isExpanded)"
                  >
                    {{ props.item.company_employee.employee.person.name }}
                  </u>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border':'border-bottom' ">
                  <div class="text-center">
                    <u
                      class="font-weight-bold cursor-pointer"
                      @click="props.expand(!props.isExpanded)"
                    >
                      {{ props.item.status }}
                    </u>
                  </div>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border text-right':'border-bottom text-right'">
                  <span class="font-weight-bold">
                    {{ currencyFormatting((props.item.payroll_cycle_employee_salary || {}).current_cycle_net_salary) }}
                  </span>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border text-center':'border-bottom text-center'">
                  <div style="display:flex">
                    <payroll-payroll-cycle-computation-dialog-edit-nett-pay
                      v-if="props.item.status === 'COMPUTATION'"
                      :nett-pay="props.item.payroll_cycle_employee_salary"
                      @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                    />

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          v-bind="attrs"
                          :loading="viewingPayslipUid === props.item.company_employee_uid"
                          :disabled="!!viewingPayslipUid && viewingPayslipUid !== props.item.company_employee_uid"
                          @click="getDocument(props.item)"
                          v-on="on"
                        >
                          <v-icon 
                            :color="props.item.is_payslip_sent === 1 ? 'green' : 'red'"
                          >
                            mdi-checkbook
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>View Payslip</span>
                    </v-tooltip>

                    <v-tooltip
                      v-if="$hasPermissionTo(['view payroll explanations']) && props.item.payroll_cycle_employee_document"
                      bottom
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          v-bind="attrs"
                          @click="explanation(props.item.payroll_cycle_employee_document)"
                          v-on="on"
                        >
                          <v-icon color="red">
                            mdi-file-pdf-box
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>Payroll Explanation</span>
                    </v-tooltip>
                  </div>
                </td>
              </tr>
            </template>
              
            <template v-slot:expanded-item="{ headers, item }">   
              <td
                class="details grey-background"
                :colspan="12"
              >
                <v-row no-gutters>
                  <v-col
                    cols="4"
                    class="pt-0"
                  >
                    <v-row
                      v-if="item.payroll_cycle_transaction.payroll_cycle.include_basic_salary || item.payroll_cycle_employee_salary.current_cycle_basic_salary > 0"
                      no-gutters
                    >
                      <v-col
                        cols="6"
                        class="font-weight-bold"
                      >
                        Basic Salary
                      </v-col>
                      <v-col cols="1">
                        :
                      </v-col>
                      <v-col
                        cols="4"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.current_cycle_basic_salary) }}
                      </v-col>

                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-basic-salary
                          v-if="item.status !=='CLOSED'"
                          :basic-salary="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                    </v-row>

                    <v-row
                      v-for="doe in item.payroll_transaction_details"
                      :key="doe.id"
                      no-gutters
                    >
                      <v-col
                        cols="6"
                        class="font-weight-bold"
                      >
                        {{ doe.deduction_or_earning.name }}
                      </v-col>
                      <v-col cols="1">
                        :
                      </v-col>
                      <v-col
                        cols="4"
                        class="text-right"
                      >
                        {{ doe.deduction_or_earning.is_negative ? '-': '' }} {{ currencyFormatting(doe.amount) }}
                      </v-col> 
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-doe
                          v-if="item.status !=='CLOSED'"
                          :doe="doe"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col
                    cols="8"
                    class="py-0 my-0"
                  >
                    <v-row no-gutters>
                      <v-col cols="4" />
                      <v-col
                        cols="4"
                        class="font-weight-bold text-center"
                      >
                        Employee
                      </v-col>
                      <v-col
                        cols="4"
                        class="font-weight-bold text-center"
                      >
                        Employer
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col
                        cols="4"
                        class="font-weight-bold text-right"
                      >
                        EPF
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.current_cycle_epf_employee_contributed) }}
                      </v-col>
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-statutory
                          v-if="item.status !=='CLOSED'"
                          header="Employee EPF"
                          :statutory="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.current_cycle_epf_employer_contributed) }}
                      </v-col>
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-statutory
                          v-if="item.status !=='CLOSED'"
                          header="Employer EPF"
                          :statutory="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col
                        cols="4"
                        class="font-weight-bold text-right"
                      >
                        SOCSO
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.current_cycle_socso_employee_contributed) }}
                      </v-col>
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-statutory
                          v-if="item.status !=='CLOSED'"
                          header="Employee SOCSO"
                          :statutory="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.current_cycle_socso_employer_contributed) }}
                      </v-col>
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-statutory
                          v-if="item.status !=='CLOSED'"
                          header="Employer SOCSO"
                          :statutory="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col
                        cols="4"
                        class="font-weight-bold text-right"
                      >
                        SKBBK
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.current_cycle_socso_skbbk_employee_contributed) }}
                      </v-col>
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-statutory
                          v-if="item.status !=='CLOSED'"
                          header="Employee SKBBK"
                          :statutory="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        -
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col
                        cols="4"
                        class="font-weight-bold text-right"
                      >
                        EIS
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.current_cycle_eis_employee_contributed) }}
                      </v-col>
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-statutory
                          v-if="item.status !=='CLOSED'"
                          header="Employee EIS"
                          :statutory="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.current_cycle_eis_employer_contributed) }}
                      </v-col>
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-statutory
                          v-if="item.status !=='CLOSED'"
                          header="Employer EIS"
                          :statutory="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col
                        cols="4"
                        class="font-weight-bold text-right"
                      >
                        PCB
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.current_cycle_employee_pcb_paid) }}
                      </v-col>
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-statutory
                          v-if="item.status !=='CLOSED'"
                          header="Employee PCB"
                          :statutory="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.current_cycle_employer_pcb_paid) }}
                      </v-col>
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-statutory
                          v-if="item.status !=='CLOSED'"
                          header="Employer PCB"
                          :statutory="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col
                        cols="4"
                        class="font-weight-bold text-right"
                      >
                        HRDF
                      </v-col>
                      <v-col
                        cols="3"
                        class="text-right"
                      >
                        -
                      </v-col>
                      <v-col
                        cols="4"
                        class="text-right"
                      >
                        {{ currencyFormatting(item.payroll_cycle_employee_salary.employer_hrdf) }}
                      </v-col>
                      <v-col cols="1">
                        <payroll-payroll-cycle-computation-dialog-edit-statutory
                          v-if="item.status !=='CLOSED'"
                          header="Employer HRDF"
                          :statutory="item.payroll_cycle_employee_salary"
                          @updated="loadPayrollCycleComputationSummary(payrollTransactionId)"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </td>
            </template> 
          </v-data-table>
        </v-skeleton-loader>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
  export default {
    components: {
      PayrollPayrollCycleComputationDialogEditDoe: () => import('@/components/hris/payroll/payroll-cycle/computation/DialogEditDoe'),
      PayrollPayrollCycleComputationDialogEditBasicSalary: () => import('@/components/hris/payroll/payroll-cycle/computation/DialogEditBasicSalary'),
      PayrollPayrollCycleComputationDialogEditStatutory: () => import('@/components/hris/payroll/payroll-cycle/computation/DialogEditStatutory'),
      PayrollPayrollCycleComputationDialogEditNettPay: () => import('@/components/hris/payroll/payroll-cycle/computation/DialogEditNettPay'),
      PayrollPayrollCycleComputationDialogRemoveInactivePayrollCycleEmployees: () => import('@/components/hris/payroll/payroll-cycle/computation/DialogRemoveInactivePayrollCycleEmployees'),
      PayrollPayrollCycleComputationDialogListMissingExplanationPayrollCycleEmployees: () => import('@/components/hris/payroll/payroll-cycle/computation/DialogListMissingExplanationPayrollCycleEmployees'),
    },
    props: {
      payrollPeriod: {
        type: Object,
        required: true,
      },      
    },
    data () {
      return {
        computationSummaries: [],
        headers: [
          {
            text: 'Company Name',
            align: 'left',
            value: 'company_employee.company.name',
          },
          {
            text: 'Employee Name',
            align: 'left',
            value: 'company_employee.employee.person.name',
          },
          {
            text: 'Status',
            align: 'center',
            value: 'status',
          },
          {
            text: 'Nett Pay (RM)',
            align: 'right',
            value: 'payroll_cycle_employee_salary.current_cycle_net_salary',
          },
          {
            text: '',
            sortable: false,
            value: 'action',
          },
        ],
        payrollTransactionId: null,
        search: null,
        loading: false,
        viewingPayslipUid: null,
        inactivePayrollCycleEmployees: [],
        explanationReportPayrollCycleEmployees: [],
      }
    },
    methods: {
      loadPayrollCycleComputationSummary (payrollCycleTransactionId) {
        this.loading = true
        this.payrollTransactionId = payrollCycleTransactionId

        this.$api.computationSummary().list(payrollCycleTransactionId)
          .then(res => {
            this.computationSummaries = _.orderBy(res.data, ['company_employee.company.name', 'company_employee.employee.person.name'], ['asc', 'asc'])

            this.explanationReportPayrollCycleEmployees = this.computationSummaries.filter(summary => { return !summary.payroll_cycle_employee_document })

            this.loading = false
          })

        this.$api.payrollCycleEmployees().listInactive(payrollCycleTransactionId)
          .then(res => {
            this.inactivePayrollCycleEmployees = res.data
          })
      },
      explanation (payrollCycleEmployeeDocument) {
        window.open(payrollCycleEmployeeDocument.file_url + '?v=' + this.$moment().unix(), '_blank')
      },
      currencyFormatting (item) {
        let value = (Math.round((item / 100) * 100) / 100).toFixed(2)
        return this.thousandSeparator(value)
      },
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
      
      clearComputationSummary () {
        this.computationSummaries = []
      },
      getDocument (value) {
        // prevent duplicate requests while the payslip PDF is still being generated
        if (this.viewingPayslipUid) return

        this.viewingPayslipUid = value.company_employee_uid

        this.$api.payslips().download(value.company_employee_uid, this.payrollTransactionId, 'view')
          .then(res => {
            let blob = new Blob([res.data], { type: 'application/pdf' })
            let link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.setAttribute('target', value.company_employee.employee.person.name + '(' + value.company_employee.company.name + ')' + '.pdf')
            link.click()
          })
          .finally(() => {
            this.viewingPayslipUid = null
          })
      },      
    },
  }
</script>

<style scoped>
.remove-bottom-border {
  border-bottom: 0px !important
}
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important
}

.details {
  padding-left:85px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding-right:85px;
}

.cursor-pointer{
  cursor: pointer;
} 

</style>
